import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../hooks/useAuth';
import { instantWellnessService } from '../services/instantWellnessService';
import jsPDF from 'jspdf';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Badge,
  Divider,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  FlashOn,
  Send,
  Psychology,
  Lightbulb,
  SelfImprovement,
  Download,
  AutoAwesome,
  Bolt,
  Star,
  TrendingUp,
  Timer,
  MoreVert,
  BookOutlined,
  Analytics,
  Settings,
  Logout,
  MonetizationOn,
  People,
  SportsEsports,
  EmojiObjects,
  Archive,
  EmojiEvents
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const InstantWellness = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [inputText, setInputText] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Helper function to safely render response items that might be objects or strings
  const renderResponseItem = (item) => {
    if (typeof item === 'string') {
      return item;
    } else if (typeof item === 'object' && item !== null) {
      // Handle objects with description property
      if (item.description) {
        return item.description;
      }
      // Handle objects with intensity and description
      if (item.intensity && item.description) {
        return `${item.description} (${item.intensity})`;
      }
      // Fallback: stringify the object
      return JSON.stringify(item);
    }
    return String(item);
  };

  // Check token balance and deduct tokens
  const checkAndDeductTokens = async (tokensToDeduct) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('token_balance')
        .eq('id', session.user.id)
        .single();

      if (profileError) throw profileError;

      if (profile.token_balance < tokensToDeduct) {
        setError(`Insufficient tokens. You need ${tokensToDeduct} tokens but only have ${profile.token_balance}. Redirecting to get more tokens...`);
        setTimeout(() => {
          navigate('/get-tokens');
        }, 3000);
        return false;
      }

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ token_balance: profile.token_balance - tokensToDeduct })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      setTokenBalance(profile.token_balance - tokensToDeduct);
      return true;
    } catch (err) {
      console.error('Error checking/deducting tokens:', err);
      setError('Failed to process token deduction');
      return false;
    }
  };

  // Fetch token balance
  const fetchTokenBalance = useCallback(async () => {
    if (!session?.user?.id) {
      return;
    }
    
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('token_balance')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;
      setTokenBalance(profile.token_balance);
    } catch (err) {
      console.error('Error fetching token balance:', err);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.id) {
      fetchTokenBalance();
    }
  }, [session?.user?.id, fetchTokenBalance]);

  const handleSubmit = async () => {
    if (!inputText.trim()) {
      setError('Please enter something to get wellness guidance');
      return;
    }

    // Check and deduct 5 tokens before processing
    const tokenDeducted = await checkAndDeductTokens(5);
    if (!tokenDeducted) {
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Use local instant wellness service
      const data = await instantWellnessService.getWellnessGuidance(
        inputText.trim(),
        session?.user?.id,
        session // Pass the session object for authentication
      );

      setResponse(data);
    } catch (err) {
      console.error('Error getting instant wellness response:', err);
      setError('Failed to get wellness guidance. Please try again.');
      
      // Refund tokens if request failed
      try {
        await supabase
          .from('user_profiles')
          .update({ token_balance: tokenBalance + 5 })
          .eq('id', session.user.id);
        setTokenBalance(tokenBalance + 5);
      } catch (refundError) {
        console.error('Error refunding tokens:', refundError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  const downloadPDF = () => {
    if (!response) return;

    const doc = new jsPDF();
    let yPosition = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 7;

    // Helper function to add text with word wrapping
    const addText = (text, x, y, maxWidth, fontSize = 12, fontStyle = 'normal') => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      const lines = doc.splitTextToSize(text, maxWidth);
      
      lines.forEach((line, index) => {
        // Check if we need a new page
        if (y + (index * lineHeight) > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, x, y + (index * lineHeight));
      });
      
      return y + (lines.length * lineHeight);
    };

    // Add title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Your Personalized Wellness Guide', margin, yPosition);
    yPosition += 15;

    // Add date
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 15;

    // Add user query
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Your Question:', margin, yPosition);
    yPosition += 10;
    
    yPosition = addText(inputText, margin + 5, yPosition, 165, 11);
    yPosition += 15;

    // Add Immediate Relief
    if (response.immediateRelief && response.immediateRelief.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Immediate Relief:', margin, yPosition);
      yPosition += 10;
      
      response.immediateRelief.forEach((relief, index) => {
        yPosition = addText(`• ${relief}`, margin + 5, yPosition, 165, 11);
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Add Breathing Exercises
    if (response.breathingExercises && response.breathingExercises.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Breathing Exercises:', margin, yPosition);
      yPosition += 10;
      
      response.breathingExercises.forEach((exercise, index) => {
        yPosition = addText(`• ${exercise}`, margin + 5, yPosition, 165, 11);
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Add Cognitive Strategies
    if (response.cognitiveStrategies && response.cognitiveStrategies.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Cognitive Strategies:', margin, yPosition);
      yPosition += 10;
      
      response.cognitiveStrategies.forEach((strategy, index) => {
        yPosition = addText(`• ${strategy}`, margin + 5, yPosition, 165, 11);
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Add Physical Actions
    if (response.physicalActions && response.physicalActions.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Physical Actions:', margin, yPosition);
      yPosition += 10;
      
      response.physicalActions.forEach((action, index) => {
        yPosition = addText(`• ${action}`, margin + 5, yPosition, 165, 11);
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Add Long-term Strategies
    if (response.longTermStrategies && response.longTermStrategies.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Long-term Strategies:', margin, yPosition);
      yPosition += 10;
      
      response.longTermStrategies.forEach((strategy, index) => {
        yPosition = addText(`• ${strategy}`, margin + 5, yPosition, 165, 11);
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Add Personal Affirmation
    if (response.affirmation) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Personal Affirmation:', margin, yPosition);
      yPosition += 10;
      
      yPosition = addText(`"${response.affirmation}"`, margin + 5, yPosition, 165, 11, 'italic');
      yPosition += 15;
    }

    // Add Emotional State
    if (response.emotionalState) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Emotional Insight:', margin, yPosition);
      yPosition += 10;
      
      yPosition = addText(response.emotionalState, margin + 5, yPosition, 165, 11);
      yPosition += 10;
    }

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(
        'Generated by Wellness AI - For informational purposes only',
        margin,
        pageHeight - 10
      );
    }

    // Save the PDF
    const fileName = `wellness-guidance-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const quickPrompts = [
    "I'm feeling anxious about work",
    "I need motivation to exercise",
    "I'm having trouble sleeping",
    "I feel overwhelmed today",
    "I need a quick mood boost",
    "I'm struggling with self-confidence"
  ];

  const handleQuickPrompt = (prompt) => {
    setInputText(prompt);
  };

  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogoutFromMenu = async () => {
    handleMenuClose();
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: '#fafbfc', 
      minHeight: '100vh',
      '& @keyframes pulse': {
        '0%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.7, transform: 'scale(1.1)' },
        '100%': { opacity: 1, transform: 'scale(1)' }
      }
    }}>
      {/* Progress Dashboard Style Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e4e7',
        py: { xs: 2, sm: 3 }
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between"
               flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}
                 flexDirection={{ xs: 'column', sm: 'row' }} textAlign={{ xs: 'center', sm: 'left' }}>
              <IconButton 
                onClick={() => navigate('/conversation')}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
              >
                <ArrowBack sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
              
              <Box>
                <Typography variant="h4" fontWeight="600" color="#1a1a1a"
                           fontSize={{ xs: '1.5rem', sm: '2rem', md: '2.125rem' }}>
                  Instant Wellness ⚡
                </Typography>
                <Typography variant="body1" color="#6b7280" sx={{ mt: 0.5 }}
                           fontSize={{ xs: '0.875rem', sm: '1rem' }}>
                  Get instant, AI-powered wellness guidance in seconds
                </Typography>
              </Box>
            </Box>

            <Badge badgeContent={tokenBalance} color="primary">
              <IconButton 
                onClick={handleMenuOpen}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
              >
                <MoreVert sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Badge>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          
          {/* Gamified Stats Row */}
          <Grid item xs={4} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                  <Box sx={{ 
                    width: { xs: 40, sm: 48 }, 
                    height: { xs: 40, sm: 48 }, 
                    borderRadius: 2,
                    bgcolor: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    animation: 'pulse 2s infinite'
                  }}>
                    <Bolt sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="700" color="#1a1a1a"
                             fontSize={{ xs: '1.8rem', sm: '2.5rem', md: '3rem' }}>
                    {tokenBalance}
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '1rem', sm: '1.25rem' }}>
                    Tokens
                  </Typography>
                  <Typography variant="body2" color="#6b7280"
                             fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                    Use wisely! ⚡
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
                  <Box sx={{ 
                    width: { xs: 40, sm: 48 }, 
                    height: { xs: 40, sm: 48 }, 
                    borderRadius: 2,
                    bgcolor: '#f093fb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    animation: 'pulse 2s infinite'
                  }}>
                    <Timer sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="700" color="#1a1a1a"
                             fontSize={{ xs: '1.5rem', sm: '2.5rem', md: '3rem' }}>
                    &lt;30s
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '0.9rem', sm: '1.25rem' }}>
                    Response Time
                  </Typography>
                  <Typography variant="body2" color="#6b7280"
                             fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                    Lightning fast! 🚀
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12}>
            <Card sx={{ 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                
                <Typography variant="h5" fontWeight="700" color="#1a1a1a" gutterBottom>
                  What's on your mind? 💭
                </Typography>
                
                <Box display="flex" gap={2} mb={3}>
                  <TextField
                    label="Ask a wellness question..."
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    onKeyDown={handleKeyPress}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ 
                      bgcolor: '#fafbfc',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover': { borderColor: '#667eea' },
                        '&.Mui-focused': { borderColor: '#667eea' }
                      }
                    }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={loading || !inputText.trim()}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 700,
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      minWidth: 120,
                      boxShadow: '0 4px 12px rgba(102,126,234,0.25)',
                      '&:hover': { 
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Send'}
                  </Button>
                </Box>

                {/* Quick Prompts */}
                <Typography variant="h6" fontWeight="600" color="#1a1a1a" gutterBottom>
                  Quick Prompts ✨
                </Typography>
                <Grid container spacing={1} mb={3}>
                  {quickPrompts.map((prompt, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={prompt}
                        onClick={() => handleQuickPrompt(prompt)}
                        sx={{ 
                          bgcolor: 'rgba(102,126,234,0.1)',
                          color: '#667eea',
                          fontWeight: 500,
                          '&:hover': { bgcolor: 'rgba(102,126,234,0.2)' },
                          cursor: 'pointer'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>

                {loading && (
                  <Box textAlign="center" py={4}>
                    <CircularProgress sx={{ color: '#667eea' }} />
                    <Typography variant="body2" color="#6b7280" sx={{ mt: 2 }}>
                      Generating your personalized wellness guidance...
                    </Typography>
                  </Box>
                )}

                {response && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Card sx={{ 
                      mt: { xs: 2, sm: 3 }, 
                      borderRadius: { xs: 2, sm: 3 },
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: '1px solid #e0e4e7',
                      borderLeft: '6px solid #4ecdc4',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)',
                      overflow: 'hidden'
                    }}>
                      <CardContent sx={{ p: 0 }}>
                        {/* Header */}
                        <Box sx={{ 
                          background: 'linear-gradient(135deg, #4ecdc4 0%, #44b3a6 100%)',
                          color: 'white',
                          p: { xs: 2, sm: 3 },
                          mb: { xs: 2, sm: 3 }
                        }}>
                          <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
                            <Box sx={{ 
                              width: { xs: 40, sm: 50 }, 
                              height: { xs: 40, sm: 50 }, 
                              borderRadius: 2,
                              bgcolor: 'rgba(255,255,255,0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <AutoAwesome sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                            <Box>
                              <Typography variant="h5" fontWeight="700" fontSize={{ xs: '1.1rem', sm: '1.5rem' }}>
                                Your Comprehensive Wellness Guide
                              </Typography>
                              <Typography variant="body1" sx={{ opacity: 0.9 }} fontSize={{ xs: '0.8rem', sm: '1rem' }}>
                                Evidence-based strategies tailored specifically for you
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                          {/* Progress Indicator */}
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="body2" color="#6b7280" gutterBottom>
                              Analysis Complete
                            </Typography>
                            <LinearProgress 
                              variant="determinate" 
                              value={100} 
                              sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  borderRadius: 4
                                }
                              }}
                            />
                            <Typography variant="body2" color="#10b981" sx={{ mt: 1, fontWeight: 600 }}>
                              ✓ 6 therapeutic strategies identified and personalized
                            </Typography>
                          </Box>                          {/* Immediate Relief Section */}
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1, duration: 0.5 }}
                          >
                            <Box sx={{ mb: 4 }}>
                              <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <Box sx={{ 
                                  width: 32, 
                                  height: 32, 
                                  borderRadius: 2,
                                  bgcolor: '#fef3c7',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <Bolt sx={{ color: '#f59e0b', fontSize: 18 }} />
                                </Box>
                                <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                                  Immediate Relief
                                </Typography>
                                <Chip 
                                  label="Act Now" 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: '#f59e0b', 
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }} 
                                />
                              </Box>
                              <Paper sx={{ 
                                p: 3, 
                                bgcolor: 'rgba(245, 158, 11, 0.05)', 
                                borderRadius: 2,
                                border: '1px solid rgba(245, 158, 11, 0.2)',
                                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)'
                              }}>
                                {response.immediateRelief?.map((relief, index) => (
                                  <Box key={index} display="flex" alignItems="flex-start" gap={2} mb={index < response.immediateRelief.length - 1 ? 2 : 0}>
                                    <Box sx={{ 
                                      width: 8, 
                                      height: 8, 
                                      borderRadius: '50%', 
                                      bgcolor: '#f59e0b',
                                      mt: 1,
                                      flexShrink: 0
                                    }} />
                                    <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#1a1a1a', fontWeight: 500 }}>
                                      {renderResponseItem(relief)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Paper>
                            </Box>
                          </motion.div>

                      {/* Breathing Exercises Section */}
                      {response.breathingExercises && response.breathingExercises.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <SelfImprovement sx={{ color: '#10b981', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                              Breathing Exercises
                            </Typography>
                          </Box>
                          <Paper sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(16, 185, 129, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(16, 185, 129, 0.2)'
                          }}>
                            {response.breathingExercises.map((exercise, index) => (
                              <Box key={index} display="flex" alignItems="flex-start" gap={2} mb={index < response.breathingExercises.length - 1 ? 2 : 0}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: '#10b981',
                                  mt: 1
                                }} />
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#1a1a1a' }}>
                                  {renderResponseItem(exercise)}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      )}

                      {/* Cognitive Strategies Section */}
                      {response.cognitiveStrategies && response.cognitiveStrategies.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Psychology sx={{ color: '#8b5cf6', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                              Cognitive Strategies
                            </Typography>
                          </Box>
                          <Paper sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(139, 92, 246, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(139, 92, 246, 0.2)'
                          }}>
                            {response.cognitiveStrategies.map((strategy, index) => (
                              <Box key={index} display="flex" alignItems="flex-start" gap={2} mb={index < response.cognitiveStrategies.length - 1 ? 2 : 0}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: '#8b5cf6',
                                  mt: 1
                                }} />
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#1a1a1a' }}>
                                  {renderResponseItem(strategy)}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      )}

                      {/* Physical Actions Section */}
                      {response.physicalActions && response.physicalActions.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <TrendingUp sx={{ color: '#f97316', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                              Physical Actions
                            </Typography>
                          </Box>
                          <Paper sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(249, 115, 22, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(249, 115, 22, 0.2)'
                          }}>
                            {response.physicalActions.map((action, index) => (
                              <Box key={index} display="flex" alignItems="flex-start" gap={2} mb={index < response.physicalActions.length - 1 ? 2 : 0}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: '#f97316',
                                  mt: 1
                                }} />
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#1a1a1a' }}>
                                  {renderResponseItem(action)}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      )}

                      {/* Long-term Strategies Section */}
                      {response.longTermStrategies && response.longTermStrategies.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Timer sx={{ color: '#6366f1', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                              Long-term Strategies
                            </Typography>
                          </Box>
                          <Paper sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(99, 102, 241, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(99, 102, 241, 0.2)'
                          }}>
                            {response.longTermStrategies.map((strategy, index) => (
                              <Box key={index} display="flex" alignItems="flex-start" gap={2} mb={index < response.longTermStrategies.length - 1 ? 2 : 0}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: '#6366f1',
                                  mt: 1
                                }} />
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#1a1a1a' }}>
                                  {renderResponseItem(strategy)}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        </Box>
                      )}

                      {/* Personalized Affirmation Section */}
                      {response.affirmation && (
                        <Box sx={{ mb: 4 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Star sx={{ color: '#ec4899', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                              Personal Affirmation
                            </Typography>
                          </Box>
                          <Paper sx={{ 
                            p: 3, 
                            bgcolor: 'rgba(236, 72, 153, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(236, 72, 153, 0.2)',
                            textAlign: 'center'
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontStyle: 'italic', 
                              color: '#1a1a1a',
                              lineHeight: 1.6,
                              fontWeight: 500
                            }}>
                              "{response.affirmation}"
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      {/* Emotional State Insight */}
                      {response.emotionalState && (
                        <Box sx={{ mb: 4 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Lightbulb sx={{ color: '#14b8a6', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                              Emotional Insight
                            </Typography>
                          </Box>
                          <Paper sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(20, 184, 166, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(20, 184, 166, 0.2)'
                          }}>
                            <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#1a1a1a' }}>
                              {response.emotionalState}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      
                      {/* Action Buttons */}
                      <Box display="flex" gap={2} mt={4}>
                        <Button 
                          onClick={downloadPDF} 
                          variant="contained"
                          startIcon={<Download />}
                          sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(102,126,234,0.25)',
                            '&:hover': { 
                              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          Download Complete Guide
                        </Button>
                        <Button 
                          onClick={() => setResponse(null)} 
                          variant="outlined"
                          sx={{ 
                            color: '#6b7280',
                            borderColor: '#d1d5db',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            '&:hover': { 
                              bgcolor: 'rgba(107,114,128,0.1)',
                              borderColor: '#9ca3af'
                            }
                          }}
                        >
                          New Question
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Menu */}
    <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleMenuClose}>
      {/* Dopamine-producing links at the top */}
      <MenuItem onClick={() => handleMenuItemClick('/get-tokens')}>
        <ListItemIcon><MonetizationOn sx={{ color: '#FFD700' }} /></ListItemIcon>
        <ListItemText primary="Get Tokens" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/human-therapists')}>
        <ListItemIcon><People sx={{ color: '#4CAF50' }} /></ListItemIcon>
        <ListItemText primary="Human Therapists" />
      </MenuItem>
      <Divider />
      
      {/* Core features */}
      <MenuItem onClick={() => handleMenuItemClick('/conversation')}>
        <ListItemIcon><Psychology sx={{ color: '#E91E63' }} /></ListItemIcon>
        <ListItemText primary="Chat" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/journal')}>
        <ListItemIcon><BookOutlined sx={{ color: '#2196F3' }} /></ListItemIcon>
        <ListItemText primary="Journal" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/instant-wellness')}>
        <ListItemIcon><FlashOn sx={{ color: '#FF9800' }} /></ListItemIcon>
        <ListItemText primary="Instant Wellness" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/intelligent-meditation')}>
        <ListItemIcon><Psychology sx={{ color: '#9C27B0' }} /></ListItemIcon>
        <ListItemText primary="Meditation" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/progress')}> 
        <ListItemIcon><Analytics sx={{ color: '#00BCD4' }} /></ListItemIcon>
        <ListItemText primary="Progress" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/challenges')}>
        <ListItemIcon><SportsEsports sx={{ color: '#FF5722' }} /></ListItemIcon>
        <ListItemText primary="Challenges" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/mind-games')}>
        <ListItemIcon><EmojiObjects sx={{ color: '#9C27B0' }} /></ListItemIcon>
        <ListItemText primary="Mind Games" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/leaderboard')}>
        <ListItemIcon><EmojiEvents sx={{ color: '#FFC107' }} /></ListItemIcon>
        <ListItemText primary="Leaderboard" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/conversation')}>
        <ListItemIcon><Settings sx={{ color: '#757575' }} /></ListItemIcon>
        <ListItemText primary="Settings" />
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogoutFromMenu}>
        <ListItemIcon><Logout sx={{ color: '#F44336' }} /></ListItemIcon>
        <ListItemText primary="Logout" />
      </MenuItem>
    </Menu>
  </Container>
</Box>
  );
};

export default InstantWellness;
